import { useEffect, useMemo, useState } from "react";
import { Search, RefreshCw, X } from "lucide-react";
import { useBotLogs } from "../../hooks/useBotLogs";
import {
  STATUS_FILTER_ORDER,
  getStatusInfo,
} from "../../utils/statusLabels";
import MessageTimelineCard from "../MessageTimelineCard";
import {
  DateRangeSelector,
  StatusChip,
  ListSkeleton,
  EmptyState,
  computeDateRange,
} from "../_logFilters";

/**
 * Tab "Tin nhắn" — the headline feature.
 * Filter bar (date / status / search) + scrollable list of MessageTimelineCard.
 */
const BotMessagesTimeline = ({ bot, shopsList }) => {
  const [datePreset, setDatePreset] = useState("7d");
  const [customRange, setCustomRange] = useState({ dateFrom: "", dateTo: "" });
  const [statusSet, setStatusSet] = useState(() => new Set());
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset filters when switching bots
  useEffect(() => {
    setDatePreset("7d");
    setStatusSet(new Set());
    setSearch("");
    setSearchInput("");
  }, [bot?.id]);

  const dateRange = useMemo(() => {
    if (datePreset === "custom") {
      const dateFrom = customRange.dateFrom
        ? new Date(customRange.dateFrom).toISOString()
        : undefined;
      const dateTo = customRange.dateTo
        ? new Date(`${customRange.dateTo}T23:59:59`).toISOString()
        : undefined;
      return { dateFrom, dateTo };
    }
    return computeDateRange(datePreset);
  }, [datePreset, customRange]);

  const statusArr = useMemo(() => Array.from(statusSet), [statusSet]);

  const { rows, total, loading, loadingMore, error, hasMore, loadMore, refresh } =
    useBotLogs(bot.id, {
      status: statusArr.length > 0 ? statusArr : undefined,
      dateFrom: dateRange.dateFrom,
      dateTo: dateRange.dateTo,
      search: search || undefined,
    });

  const toggleStatus = (s) => {
    setStatusSet((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const clearFilters = () => {
    setStatusSet(new Set());
    setSearchInput("");
    setSearch("");
    setDatePreset("7d");
    setCustomRange({ dateFrom: "", dateTo: "" });
  };

  const hasActiveFilter =
    statusSet.size > 0 || search || datePreset !== "7d";

  return (
    <div className="max-w-4xl mx-auto">
      {/* Sticky filter bar */}
      <div className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/80 border-b border-gray-200">
        <div className="px-6 py-3 space-y-2.5">
          <div className="flex items-center gap-2 flex-wrap">
            <DateRangeSelector
              datePreset={datePreset}
              setDatePreset={setDatePreset}
              customRange={customRange}
              setCustomRange={setCustomRange}
            />

            <div className="ml-auto flex items-center gap-2">
              <div className="text-xs text-gray-500">
                {loading
                  ? "Đang tải…"
                  : `${rows.length}${total > rows.length ? `/${total}` : ""} tin`}
              </div>
              <button
                type="button"
                onClick={refresh}
                disabled={loading}
                className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 px-2 py-1 rounded-md hover:bg-gray-100 disabled:opacity-50"
                title="Tải lại"
              >
                <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                Làm mới
              </button>
            </div>
          </div>

          {/* Status chips */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <StatusChip
              label="Tất cả"
              active={statusSet.size === 0}
              onClick={() => setStatusSet(new Set())}
            />
            {STATUS_FILTER_ORDER.map((s) => {
              const info = getStatusInfo(s);
              const active = statusSet.has(s);
              return (
                <StatusChip
                  key={s}
                  label={info.label}
                  active={active}
                  dot={info.dot}
                  onClick={() => toggleStatus(s)}
                />
              );
            })}
          </div>

          {/* Search row */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search
                size={13}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Tìm theo tên khách, nội dung tin nhắn..."
                className="w-full pl-8 pr-8 py-1.5 text-sm rounded-md border border-gray-200 bg-white focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 outline-none transition"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={13} />
                </button>
              )}
            </div>
            {hasActiveFilter && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium px-2 py-1 rounded hover:bg-indigo-50"
              >
                Xóa bộ lọc
              </button>
            )}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="px-6 py-4 space-y-2.5">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3">
            Lỗi tải tin nhắn: {error}
          </div>
        )}

        {loading && rows.length === 0 ? (
          <ListSkeleton />
        ) : rows.length === 0 ? (
          <EmptyState
            hasFilter={hasActiveFilter}
            onClear={clearFilters}
            descEmpty="Bot chưa gửi tin nhắn nào trong khoảng thời gian này. Tin nhắn sẽ xuất hiện ở đây khi bot bắt đầu hoạt động."
          />
        ) : (
          <>
            {rows.map((log) => (
              <MessageTimelineCard
                key={log.id}
                log={log}
                shopsList={shopsList}
              />
            ))}

            {hasMore && (
              <div className="pt-3 flex justify-center">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="px-4 py-2 text-sm font-medium text-indigo-600 bg-white border border-indigo-200 rounded-md hover:bg-indigo-50 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {loadingMore && (
                    <RefreshCw size={14} className="animate-spin" />
                  )}
                  Tải thêm tin
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BotMessagesTimeline;
